import { decodeUpdate } from 'yjs';

import type { PublicationRefusal } from './refused-publications';

export interface DocClock {
  docId: string;
  timestamp: Date;
}

/**
 * Identity of the local stores (IndexedDB name, SQLite file and pool
 * namespace). Frozen: changing it orphans every local store. Schema changes go
 * through the store migrators, never through this value.
 */
export const LOCAL_STORE_NAMESPACE = 'local-publication-v1';
/**
 * Format tag of persisted publication journal entries (field `formatEpoch`,
 * SQLite column `format_epoch`). A new tag ships only together with a reader
 * for every older tag. Independent of the sync protocol version.
 */
// v2: entries also hold companions; a v1 entry has none.
export const PUBLICATION_JOURNAL_FORMAT = 'local-publication-v2';
/** Older tags the journal still reads. */
export const PUBLICATION_JOURNAL_FORMATS_READ = [
  'local-publication-v1',
  PUBLICATION_JOURNAL_FORMAT,
] as const;
/**
 * Version of the sync wire contract: space:join, space:push-doc-publication,
 * space:load-doc-publication and their answers. Every change to a request or
 * answer shape of these events is SYNC_PROTOCOL_VERSION + 1; a server answers a
 * connection only in shapes of the version it accepted on join.
 */
// 2: clients write pages in page format 2 (the page structure); a client of
// version 1 writes sys:children and is refused on join.
// 3: clients also write Canvas block layers (l); version 2 clients omit l
// and are refused by the same join check.
// 4: a create group carries the page that hosts its new database, which the
// server binds in the same commit; version 3 clients register the binding
// over HTTP instead and are refused on join.
export const SYNC_PROTOCOL_VERSION = 4;

/** Message of the SYNC_PROTOCOL_UNSUPPORTED join refusal. */
export const SYNC_PROTOCOL_UNSUPPORTED_MESSAGE =
  'This app version is no longer supported by the server';

export type PublicationKind =
  | 'create'
  | 'update'
  | 'delete'
  | 'move'
  | 'schema';

export interface PublicationActor {
  readonly clientId: number;
  readonly nextClock: number;
}

export interface PublicationMemberBase {
  readonly docId: string;
  /** Merged local bytes including pending deltas; null if absent. */
  readonly bin: Uint8Array | null;
  /** Exactly the clock commitPublication compares as expectedClock. */
  readonly clock: Date | null;
  /** Persisted publication actor of this store/doc; null before first allocation. */
  readonly actor: PublicationActor | null;
}

/** A stored document's position without its bytes. Every content write
 * advances the clock; compaction keeps it. A writer that kept the document open
 * since this head continues on it only while clock and actor still match. */
export interface PublicationMemberHead {
  readonly docId: string;
  readonly clock: Date | null;
  readonly actor: PublicationActor | null;
}

export interface PublicationMember {
  readonly docId: string;
  readonly update: Uint8Array;
  /** Local adapter CAS; never a PostgreSQL sequence. */
  readonly expectedClock: Date | null;
  /** Null only for a transactionally verified first allocation. */
  readonly expectedActor: PublicationActor | null;
  /** Stable clientId; nextClock agrees with actual Yjs structs. */
  readonly nextActor: PublicationActor;
}

/**
 * A change of a normally synced document (the page that hosts a new database,
 * or a part of it) that travels in the group of a publication. The journal
 * keeps it only to send it again: it has no publication actor or clock, its
 * document keeps syncing as usual and is never isolated by a refusal.
 */
export interface PublicationCompanion {
  readonly docId: string;
  readonly update: Uint8Array;
}

export interface LocalPublication {
  readonly operationId: string;
  readonly workspaceId: string;
  readonly members: readonly PublicationMember[];
  /** Empty unless the group writes a normally synced document too. */
  readonly companions: readonly PublicationCompanion[];
  readonly kind: PublicationKind;
}

/** Every document the group of `publication` writes, as sent and hashed. */
export function publicationGroupMembers(
  publication: Pick<LocalPublication, 'members' | 'companions'>
): readonly Pick<PublicationMember, 'docId' | 'update'>[] {
  return [
    ...publication.members.map(({ docId, update }) => ({ docId, update })),
    ...publication.companions,
  ];
}

export interface LocalPublicationReceipt {
  readonly operationId: string;
  /** Durable, monotonically increasing journal cursor, without ACK effects. */
  readonly sequence: number;
  readonly clocks: readonly DocClock[];
  readonly state: 'LOCAL_COMMITTED';
}

export interface PublicationSendPolicy {
  readonly profile: PublicationProfile;
  readonly deadlineMs: number;
}

export interface PendingPublication {
  readonly publication: LocalPublication;
  readonly sequence: number;
  readonly mutationId: string;
  readonly state:
    | 'LOCAL_COMMITTED'
    | 'PENDING_REMOTE'
    | 'REBASING'
    | 'BLOCKED_CONFLICT';
  readonly attempt: number;
  readonly send: PublicationSendPolicy | null;
  /** Set only while BLOCKED_CONFLICT; absent on entries stored without one. */
  readonly refusal?: PublicationRefusal | null;
}

export interface BeginPublicationAttemptInput {
  readonly operationId: string;
  readonly attempt: number;
  readonly send: PublicationSendPolicy;
}

export interface AcknowledgePublicationInput {
  readonly peer: string;
  readonly operationId: string;
  readonly receipt:
    | RemotePublicationReceipt
    | Extract<PublicationAck, { readonly state: 'rejected' }>;
}

/** Origin of every update emitted by applyRemotePublication; DocSyncPeer skips it. */
/** Durable publication updates are applied live once, without a single-doc echo. */
export function localPublicationApplyOrigin(operationId: string): string {
  return `local-publication:${operationId}`;
}

export function docSyncRemoteApplyOrigin(peer: string): string {
  return `sync:${peer}:remote`;
}

export interface ApplyRemotePublicationInput {
  readonly peer: string;
  /**
   * Consecutive read views of one database with the same root precondition.
   * Each member keeps the viewId of the view it was read in; the members of
   * all views are written in one transaction.
   */
  readonly views: readonly DocPublicationView[];
}

export interface RemotePublicationLocalReceipt {
  readonly viewIds: readonly string[];
  readonly clocks: readonly DocClock[];
}

/** Internal journal states; these introduce no new visible sync UX. */
export type PublicationState =
  | 'LOCAL_COMMITTED'
  | 'PENDING_REMOTE'
  | 'REMOTE_COMMITTED'
  | 'REBASING'
  | 'BLOCKED_CONFLICT';

export type RemotePublicationState =
  | 'PENDING'
  | 'CLAIMED'
  | 'COMMITTED'
  | 'CANCELLED'
  | 'FAILED'
  | 'QUARANTINED';

export interface RemotePublicationReceipt {
  readonly groupId: string;
  readonly operationId: string;
  readonly state: RemotePublicationState;
  readonly receiptDigest?: string;
  readonly failureClass?: string;
  /** ISO timestamp of the persisted admission deadline. */
  readonly deadlineAt: string;
  /** Lossless decimal bigint strings, not local Date clocks. */
  readonly members?: readonly {
    readonly docId: string;
    readonly seq: string;
  }[];
}

export const PUBLICATION_ERROR_CODES = [
  'LOCAL_CONFLICT',
  'LOCAL_QUOTA',
  'INCOMPLETE_LOCAL_BASE',
  'FORMAT_MISMATCH',
  'FORBIDDEN',
  'REMOTE_CONFLICT',
  'LIMIT_EXCEEDED',
  'CANCELLED',
  'PERMISSION_LAG',
  'LOCKED',
] as const;

export type PublicationErrorCode = (typeof PUBLICATION_ERROR_CODES)[number];

export function isPublicationErrorCode(
  value: unknown
): value is PublicationErrorCode {
  return PUBLICATION_ERROR_CODES.some(code => code === value);
}

export class PublicationError extends Error {
  constructor(readonly code: PublicationErrorCode) {
    super(code);
    this.name = 'PublicationError';
  }
}

/**
 * The PublicationError a worker raised, from the plain error its port hands
 * over: only the wire fields cross, so `instanceof` needs the class back.
 * Any other error is returned as it is.
 */
export function revivePublicationError(error: Error): Error {
  const { code } = error as { code?: unknown };
  if (error.name !== 'PublicationError' || !isPublicationErrorCode(code))
    return error;
  const revived = Object.assign(new PublicationError(code), error);
  revived.stack = error.stack;
  return revived;
}

/**
 * Account/workspace/authEpoch come from the immutable storage handle.
 * Every operation checks persisted account authEpoch and logoutPending.
 * Missing state, an epoch mismatch or logoutPending => LOCKED, zero writes.
 * authBinding=null skips only the auth check for the account-free local
 * workspace flavour; a cloud workspace with null always fails with LOCKED.
 *
 * commitPublication checks all member clocks/actors in the same transaction
 * that writes deltas, strictly newer Date clocks, actors and journal.
 * next timestamp = max(Date.now(), previous timestamp + 1), including
 * delete-only updates with unchanged actor/state vector. Any CAS mismatch
 * => LOCAL_CONFLICT for the whole group. ACK/broadcast only after commit.
 * Quota/cancel before commit leaves no partial group. No remote call is needed.
 *
 * Journal bytes survive snapshot compaction. Duplicate ID and identical
 * mutationId return the original local receipt while journaled; different bytes
 * conflict. After remote commit, server operationId/mutationId provides idempotency.
 * Initial journal entry has attempt=0, send=null and computed mutationId.
 * Pending pages use bounded cursors, never load the complete journal.
 *
 * Budgets remain at the existing database policy and DocPersistMutationGroup
 * service (services/doc-persist/doc-persist-mutation-group.service.ts).
 * MUTATION_GROUP_*_LIMIT maps to LIMIT_EXCEEDED. An indivisible operation
 * exceeding a budget is rejected, never split to evade the budget.
 */
/** Optional, call-local timings. Missing fields mean unmeasured/skipped, never zero.
 * Sequential wall spans, including awaits. No payloads or receipt/wire fields. */
export interface PublicationCommitMeasurement {
  adapter?: 'idb';
  validationMs?: number;
  identityMs?: number;
  /** Includes earlier failed key-CAS attempts; remaining phases describe final attempt. */
  retryAndEntryMs?: number;
  attempts?: number;
  authorityReadMs?: number;
  payloadEncodeMs?: number;
  journalEncodeMs?: number;
  transactionMs?: number;
  notifyMs?: number;
}

/** Local document storage that a long-lived writer asks whether it is still
 * at the documents it holds open. */
export interface DocPublicationHeadStorage {
  /**
   * One read transaction over all docIds, in input order: the heads
   * readPublicationBase would return, without reading or merging bytes.
   */
  readPublicationHeads(
    docIds: readonly string[],
    signal?: AbortSignal
  ): Promise<readonly PublicationMemberHead[]>;
  /**
   * Reports each stored doc whose content was replaced or removed outside a
   * commit (server reset, repair, delete), also from other tabs of the store.
   * A head read before it no longer describes the stored content.
   */
  subscribeLocalDocReplaced(callback: (docId: string) => void): () => void;
}

export interface DocPublicationStorage {
  /**
   * One read transaction (snapshot) over all docIds, in input order.
   * Duplicate docId => FORMAT_MISMATCH. Same auth check (LOCKED), no writes.
   */
  readPublicationBase(
    docIds: readonly string[],
    signal?: AbortSignal
  ): Promise<readonly PublicationMemberBase[]>;
  commitPublication(
    input: LocalPublication,
    signal?: AbortSignal,
    measurement?: PublicationCommitMeasurement
  ): Promise<LocalPublicationReceipt>;
  listPendingPublications(
    after: number,
    limit: number
  ): Promise<readonly PendingPublication[]>;
  /**
   * One transaction: LOCAL_COMMITTED permits only attempt=1 and fixes send.
   * REBASING permits only stored attempt+1, with identical send policy.
   * Policy mismatch => FORMAT_MISMATCH; attempt mismatch => LOCAL_CONFLICT.
   * BLOCKED_CONFLICT => LOCAL_CONFLICT without writes.
   * Writes attempt/send and PENDING_REMOTE before any network request.
   */
  beginPublicationAttempt(
    input: BeginPublicationAttemptInput
  ): Promise<PendingPublication>;
  /**
   * A receipt with a different attempt ID or an entry outside PENDING_REMOTE
   * performs no writes. REMOTE_COMMITTED atomically deletes the journal entry
   * including member bytes and sets
   * each member's peer pushedClock to max(stored, original journal clock), never
   * the current doc clock: subsequent local edits remain pending. Preserve peer
   * clock/pulledClock; initialize a missing peer row with Date(0). Other receipt
   * states never write peer clocks: PENDING/CLAIMED retain PENDING_REMOTE;
   * REBASING and BLOCKED_CONFLICT retain their state and member bytes.
   * BLOCKED_CONFLICT stores publicationRefusalFromReceipt with the entry.
   * A duplicate ACK for a missing entry does nothing without error; server
   * operationId/mutationId supplies late duplicate idempotency.
   */
  acknowledgePublication(input: AcknowledgePublicationInput): Promise<void>;
  /**
   * Same transaction kernel: auth, all member updates, strictly newer local
   * clocks, remote marks with the viewId of each member's view. For each
   * member, set peer RemoteClock
   * and PulledRemoteClock to new Date(timestamp) in the same transaction;
   * preserve each already greater stored peer clock (max). Aborting writes
   * none of the peer clocks. Keep local pending deltas. No journal entry or
   * outbound echo; any failure aborts every write. Each emitted update uses
   * docSyncRemoteApplyOrigin(peer), which DocSyncPeer skips.
   */
  applyRemotePublication(
    input: ApplyRemotePublicationInput,
    signal?: AbortSignal
  ): Promise<RemotePublicationLocalReceipt>;
}

export type PublicationProfile =
  | 'INTERACTIVE_MICRO'
  | 'FOREGROUND_VISIBLE'
  | 'OWNER_VISIBLE_ORCHESTRATED';

/** Longest operation ID of a publication attempt the server stores. */
export const MAX_PUBLICATION_OPERATION_ID_LENGTH = 160;

/** space:push-doc-publication; editor, effects and preconditions are server-owned. */
export interface PushDocPublicationRequest {
  readonly spaceId: string;
  readonly operationId: string;
  readonly mutationId: string;
  readonly kind: PublicationKind;
  /** Fixed once in the journal before first send, unchanged on retry. */
  readonly profile: PublicationProfile;
  readonly deadlineMs: number;
  readonly members: readonly {
    readonly docId: string;
    readonly update: Uint8Array;
  }[];
}

/**
 * receipt: the group is persisted (new or existing), with every remote state,
 * including PENDING/CLAIMED after the wait budget is exhausted and terminal
 * FAILED/CANCELLED/QUARANTINED with failureClass; COMMITTED carries members.seq.
 * Feed receipts to acknowledgePublication using publicationStateFromReceipt.
 * PENDING/CLAIMED and unknown preserve PENDING_REMOTE. The next lookup is an
 * identical re-push (same operationId/bytes) under the existing failure policy;
 * there is no separate lookup wire operation. unknown is an unknown admission
 * outcome after a transaction/connection timeout, never an invented rollback.
 * rejected means nothing persisted: PERMISSION_LAG preserves PENDING_REMOTE
 * and calls wakePermissionLagDocs; STORAGE_FULL preserves PENDING_REMOTE and
 * pauses the workspace until space is free; other rejections become
 * BLOCKED_CONFLICT.
 * busy means nothing persisted: the admission had no capacity for the
 * publication and answers before durable acceptance, never after. The sender
 * keeps PENDING_REMOTE, pauses the scope for retryAfterMs and re-pushes the
 * identical operationId/bytes; workspace pauses the workspace, global the
 * connection.
 */
export type PublicationAck =
  | { readonly state: 'receipt'; readonly receipt: RemotePublicationReceipt }
  | { readonly state: 'unknown'; readonly operationId: string }
  | {
      readonly state: 'busy';
      readonly operationId: string;
      readonly scope: 'workspace' | 'global';
      readonly retryAfterMs: number;
    }
  | {
      readonly state: 'rejected';
      readonly operationId: string;
      readonly code: PublicationRejectionCode;
    };

export type PublicationRejectionCode =
  | 'FORBIDDEN'
  | 'REMOTE_CONFLICT'
  | 'FORMAT_MISMATCH'
  | 'LIMIT_EXCEEDED'
  | 'PERMISSION_LAG'
  | 'STORAGE_FULL';

/** Server-consistent snapshot precondition; independent of local clocks. */
export interface PublicationPrecondition {
  readonly snapshotId: string | null;
  readonly lastSeq: number;
  readonly contentHash: string | null;
}

/** space:load-doc-publication, bounded by existing group/document policies. */
export interface LoadDocPublicationRequest {
  readonly spaceId: string;
  readonly rootDocId: string;
  readonly memberDocIds: readonly string[];
}

export interface DocPublicationView {
  /** Server canonical hash of the sorted snapshot version vector. */
  readonly viewId: string;
  readonly members: readonly (PublicationPrecondition & {
    readonly docId: string;
    readonly update: Uint8Array;
    /**
     * Server doc clock in milliseconds, exactly the space:load-doc timestamp
     * for this doc at the read view. Part of the same consistent PostgreSQL
     * view; excluded from the viewId hash.
     */
    readonly timestamp: number;
  })[];
}

/**
 * LOCAL_COMMITTED -> PENDING_REMOTE before publishing.
 * Network/ACK loss, PERMISSION_LAG and STORAGE_FULL preserve PENDING_REMOTE.
 * Active publish lasts until a terminal remote receipt, not admission ACK.
 * REBASING -> PENDING_REMOTE only after journalling the next attempt counter.
 * ID is <original operationId>:retry-<n>; bytes, mutationId, profile and
 * deadlineMs stay fixed. Attempt n+1 requires terminal non-materialization
 * of n (FAILED semantic_precondition/claim_loss). No random IDs and no
 * automatic replay of terminal rejection/quarantine: only the user returns a
 * BLOCKED_CONFLICT entry to REBASING (RefusedPublicationStorage, 'retry'),
 * which sends it as the next attempt. BLOCKED_CONFLICT uses existing sync errors;
 * LOCKED uses the existing signed-out state and is never a retry/conflict.
 */
export function publicationStateFromReceipt(
  receipt: AcknowledgePublicationInput['receipt']
): Exclude<PublicationState, 'LOCAL_COMMITTED'> {
  switch (receipt.state) {
    case 'rejected':
      return isPendingRejectionCode(receipt.code)
        ? 'PENDING_REMOTE'
        : 'BLOCKED_CONFLICT';
    case 'PENDING':
    case 'CLAIMED':
      return 'PENDING_REMOTE';
    case 'COMMITTED':
      return 'REMOTE_COMMITTED';
    case 'FAILED':
      return receipt.failureClass === 'semantic_precondition' ||
        receipt.failureClass === 'claim_loss'
        ? 'REBASING'
        : 'BLOCKED_CONFLICT';
    case 'CANCELLED':
    case 'QUARANTINED':
      return 'BLOCKED_CONFLICT';
  }
}

/** Rejections that persisted nothing and keep the publication pending. */
export function isPendingRejectionCode(
  code: PublicationRejectionCode
): code is 'PERMISSION_LAG' | 'STORAGE_FULL' {
  return code === 'PERMISSION_LAG' || code === 'STORAGE_FULL';
}

const REMOTE_PUBLICATION_STATES: readonly unknown[] = [
  'PENDING',
  'CLAIMED',
  'COMMITTED',
  'CANCELLED',
  'FAILED',
  'QUARANTINED',
] satisfies readonly RemotePublicationState[];

const PUBLICATION_REJECTION_CODES: readonly unknown[] = [
  'FORBIDDEN',
  'REMOTE_CONFLICT',
  'FORMAT_MISMATCH',
  'LIMIT_EXCEEDED',
  'PERMISSION_LAG',
  'STORAGE_FULL',
] satisfies readonly PublicationRejectionCode[];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isReceiptMember(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.docId === 'string' &&
    typeof value.seq === 'string' &&
    /^\d+$/.test(value.seq)
  );
}

function isRemotePublicationReceipt(
  value: unknown,
  operationId: string
): boolean {
  return (
    isRecord(value) &&
    typeof value.groupId === 'string' &&
    value.operationId === operationId &&
    REMOTE_PUBLICATION_STATES.includes(value.state) &&
    typeof value.deadlineAt === 'string' &&
    (value.receiptDigest === undefined ||
      typeof value.receiptDigest === 'string') &&
    (value.failureClass === undefined ||
      typeof value.failureClass === 'string') &&
    (value.members === undefined ||
      (Array.isArray(value.members) && value.members.every(isReceiptMember)))
  );
}

/**
 * Accepts exactly the answer shapes of SYNC_PROTOCOL_VERSION for the attempt
 * `operationId`. Anything else throws FORMAT_MISMATCH before the journal is
 * touched, so an unknown answer never changes a pending publication.
 */
export function decodePublicationAck(
  value: unknown,
  operationId: string
): PublicationAck {
  if (!isRecord(value)) throw new PublicationError('FORMAT_MISMATCH');
  switch (value.state) {
    case 'receipt':
      if (isRemotePublicationReceipt(value.receipt, operationId))
        return value as PublicationAck;
      break;
    case 'unknown':
      if (value.operationId === operationId) return value as PublicationAck;
      break;
    case 'busy':
      if (
        value.operationId === operationId &&
        (value.scope === 'workspace' || value.scope === 'global') &&
        typeof value.retryAfterMs === 'number' &&
        Number.isFinite(value.retryAfterMs) &&
        value.retryAfterMs >= 0
      )
        return value as PublicationAck;
      break;
    case 'rejected':
      if (
        value.operationId === operationId &&
        PUBLICATION_REJECTION_CODES.includes(value.code)
      )
        return value as PublicationAck;
      break;
  }
  throw new PublicationError('FORMAT_MISMATCH');
}

export function publicationAttemptId(
  operationId: string,
  attempt: number
): string {
  if (!Number.isSafeInteger(attempt) || attempt < 1) {
    throw new PublicationError('LOCAL_CONFLICT');
  }
  return attempt === 1 ? operationId : `${operationId}:retry-${attempt - 1}`;
}

/** Validate plain publication bytes before sealing or IPC; storage still owns clock/actor CAS. */
export function validatePublicationUpdates(
  members: readonly {
    readonly update: Uint8Array;
    readonly nextActor?: PublicationActor;
    readonly expectedActor?: PublicationActor | null;
  }[]
): void {
  for (const member of members) {
    let decoded: ReturnType<typeof decodeUpdate>;
    try {
      decoded = decodeUpdate(member.update);
    } catch {
      throw new PublicationError('FORMAT_MISMATCH');
    }
    const next = member.nextActor;
    // Inbound updates have no locally allocated publication actor.
    if (!next) continue;
    if (
      !Number.isSafeInteger(next.clientId) ||
      next.clientId < 0 ||
      !Number.isSafeInteger(next.nextClock) ||
      next.nextClock < 0
    )
      throw new PublicationError('FORMAT_MISMATCH');
    // Delete-only deltas preserve the allocated actor clock.
    let nextClock = member.expectedActor?.nextClock ?? 0;
    for (const struct of decoded.structs) {
      if (struct.id.client === next.clientId)
        nextClock = Math.max(nextClock, struct.id.clock + struct.length);
    }
    if (next.nextClock !== nextClock)
      throw new PublicationError('FORMAT_MISMATCH');
  }
}

/** Validate companion bytes and that no document is both member and companion. */
export function validatePublicationCompanions(
  publication: Pick<LocalPublication, 'members' | 'companions'>
): void {
  const members = new Set(publication.members.map(member => member.docId));
  const seen = new Set<string>();
  for (const companion of publication.companions) {
    if (
      members.has(companion.docId) ||
      seen.has(companion.docId) ||
      companion.update.byteLength === 0
    )
      throw new PublicationError('FORMAT_MISMATCH');
    seen.add(companion.docId);
    try {
      decodeUpdate(companion.update);
    } catch {
      throw new PublicationError('FORMAT_MISMATCH');
    }
  }
}

/**
 * Leading hash frames of every MutationId. Frozen: stored MutationIds and the
 * server's idempotency depend on exactly these bytes.
 */
const MUTATION_ID_SCHEME = [
  'local-publication-mutation-v1',
  'local-publication-v1',
] as const;

/** No auth, actor, local clock, operation ID or server precondition in this hash. */
export async function computePublicationMutationId(input: {
  readonly kind: PublicationKind;
  readonly members: readonly Pick<PublicationMember, 'docId' | 'update'>[];
}): Promise<string> {
  const encoder = new TextEncoder();
  const seen = new Set<string>();
  const members = input.members.map(member => {
    if (seen.has(member.docId) || member.update.byteLength === 0) {
      throw new PublicationError('FORMAT_MISMATCH');
    }
    seen.add(member.docId);
    return {
      docId: member.docId,
      docIdBytes: encoder.encode(member.docId),
      update: new Uint8Array(member.update),
    };
  });
  members.sort((a, b) => {
    const length = Math.min(a.docIdBytes.length, b.docIdBytes.length);
    for (let index = 0; index < length; index++) {
      const difference = a.docIdBytes[index] - b.docIdBytes[index];
      if (difference !== 0) return difference;
    }
    return a.docIdBytes.length - b.docIdBytes.length;
  });

  const hex = (buffer: ArrayBuffer) =>
    Array.from(new Uint8Array(buffer), byte =>
      byte.toString(16).padStart(2, '0')
    ).join('');
  const parts = [...MUTATION_ID_SCHEME, input.kind, String(members.length)];
  for (const member of members) {
    parts.push(
      member.docId,
      hex(await crypto.subtle.digest('SHA-256', member.update)),
      String(member.update.byteLength)
    );
  }
  const frames = parts.map(part => {
    const bytes = encoder.encode(part);
    return { prefix: encoder.encode(`${bytes.byteLength}:`), bytes };
  });
  const framed = new Uint8Array(
    frames.reduce(
      (size, frame) => size + frame.prefix.length + frame.bytes.length,
      0
    )
  );
  let offset = 0;
  for (const frame of frames) {
    framed.set(frame.prefix, offset);
    offset += frame.prefix.length;
    framed.set(frame.bytes, offset);
    offset += frame.bytes.length;
  }
  return hex(await crypto.subtle.digest('SHA-256', framed));
}

/** Extended by the worker only together with concrete handlers. */
export interface DocPublicationWorkerOps {
  readPublicationBase: [readonly string[], readonly PublicationMemberBase[]];
  commitPublication: [LocalPublication, LocalPublicationReceipt];
  listPendingPublications: [
    { after: number; limit: number },
    readonly PendingPublication[],
  ];
  beginPublicationAttempt: [BeginPublicationAttemptInput, PendingPublication];
  acknowledgePublication: [AcknowledgePublicationInput, void];
  applyRemotePublication: [
    ApplyRemotePublicationInput,
    RemotePublicationLocalReceipt,
  ];
}
